import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { post } from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { storage } from '@/lib/storage';
import { getCookie, deleteCookie } from '@/lib/cookie';
import { Spinner } from '@/components/atoms/Spinner';
import { toast } from '@/store/toastStore';

/**
 * 소셜 로그인 콜백 처리
 * URL 예: /auth/callback/kakao?code=xxx&state=yyy
 */
export function AuthCallbackPage() {
  const { provider } = useParams<{ provider: 'kakao' | 'naver' | 'google' }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { setMe } = useAuthStore();

  useEffect(() => {
    const code = search.get('code');
    const state = search.get('state') ?? undefined;
    const error = search.get('error');

    if (error) {
      toast.error('소셜 로그인에 실패했어요. 다시 시도해 주세요.');
      navigate('/', { replace: true });
      return;
    }

    if (!code || !provider) {
      navigate('/', { replace: true });
      return;
    }

    (async () => {
      try {
        // 친구 추천 쿠키(sg_ref, 7일) — 신규 가입에만 의미. 기존 사용자/잘못된 코드는 백엔드가 무시.
        const referralCode = getCookie('sg_ref') ?? undefined;
        const data = await post<{
          accessToken: string;
          wasReactivated?: boolean;
          user: {
            userId: string;
            nickname: string;
            level: number;
            isPremium: boolean;
            onboardingDone: boolean;
            profileImageUrl: string | null;
            pointBalance: number;
            adminRole: string | null;
          };
        }>(`/auth/social/${provider}`, { code, state, referralCode });
        // 가입/로그인 성공 후엔 쿠키 정리 — 재로그인 시 잘못 적용되는 일 방지
        if (referralCode) deleteCookie('sg_ref');

        storage.set('accessToken', data.accessToken);
        setMe({
          userId: data.user.userId,
          nickname: data.user.nickname,
          level: data.user.level,
          score: 0,
          isPremium: data.user.isPremium,
          adminRole: data.user.adminRole ?? null,
          profileImageUrl: data.user.profileImageUrl,
          onboardingDone: data.user.onboardingDone,
        });

        // 2026-05 어뷰징 강화 — 탈퇴 계정 재활성화 시 안내 토스트
        if (data.wasReactivated) {
          toast('계정이 복구됐어요. 재가입 보너스는 지급되지 않습니다.', 'info');
        }

        navigate(data.user.onboardingDone ? '/' : '/onboarding', { replace: true });
      } catch (err) {
        console.error(err);
        toast.error('로그인에 실패했어요.');
        navigate('/', { replace: true });
      }
    })();
  }, [provider, search, navigate, setMe]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
      <Spinner size="lg" />
      <p className="text-sm text-[var(--color-text-sub)]">로그인 중이에요...</p>
    </div>
  );
}
